import { create } from "zustand"

import type {
  GetOrderLinesListQuery,
  Order,
  OrderLine,
  OrderLineListInput,
  OrderLineListSuccess,
  OrderLineSortField,
  Product,
} from "@/graphql/codegen/graphql"
import {
  GetOrderLinesListDocument,
  OrderLineSortField as OrderLineSortFieldEnum,
  SortingOrder,
} from "@/graphql/codegen/graphql"
import { adapterOrderSortFieldToOrderLineSortField } from "@/screens/Procurement/PurchaseOrders/types"
import type { SearchFilters } from "@/stores/createSearchStore"
import { createSearchStore } from "@/stores/createSearchStore"

// Types
export type ProductWithOrder = Omit<Product, "__typename"> &
  Omit<Order, "__typename"> &
  Omit<OrderLine, "__typename"> & {
    __typename: string
  }

export type OrderLinesSearchFilters = SearchFilters & {
  category?: string
  field?: OrderLineSortField
  order?: SortingOrder
  start?: string
  end?: string
}

const createQueryInput = (
  filters: OrderLinesSearchFilters,
  offset: number,
  limit: number
): { input: OrderLineListInput } => {
  const { category, field, order, start, end, query, ...otherFilters } = filters
  const adaptedField = adapterOrderSortFieldToOrderLineSortField.get(field)
  return {
    filter: {
      query,
      ...otherFilters,
      ...(start && {
        sentDateRange: {
          start,
          end,
        },
      }),
    },
    sort: { field: adaptedField ?? OrderLineSortFieldEnum.ProductId, order: order ?? SortingOrder.Desc },
    offset,
    limit,
  }
}

const deduplicateOrderLines = (newData: OrderLine[], existingData: OrderLine[]): OrderLine[] => {
  const uniqueIds = new Set(existingData.map((orderLine) => orderLine.orderLineId))
  return [...existingData, ...newData.filter((orderLine) => !uniqueIds.has(orderLine.orderLineId))]
}

// Store creation
export const useProductPurchaseOrdersSearchStore = create(
  createSearchStore<
    OrderLineListSuccess,
    OrderLinesSearchFilters,
    GetOrderLinesListQuery,
    { input: OrderLineListInput }
  >({
    documentNode: GetOrderLinesListDocument,
    deduplicateData: deduplicateOrderLines,
    initialState: {
      limit: 20,
    },
    getQueryInput: createQueryInput,
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => {
      return {
        __typename: data?.order?.getOrderLineList?.__typename,
        data:
          data?.order?.getOrderLineList?.__typename === "OrderLineListSuccess"
            ? data?.order?.getOrderLineList.orderLines
            : [],
        error:
          data?.order?.getOrderLineList.__typename === "CommonRequestError"
            ? data?.order?.getOrderLineList.error
            : undefined,
      }
    },
  })
)
